import dayjs from 'dayjs'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { Download, LayoutGrid, List } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { RevenueCombined } from '../../types'
import CustomerGroupTable from './CustomerGroupTable'
import PaymentDateTable from './PaymentDateTable'
import PaymentDateOverviewTable from './PaymentDateOverviewTable'
import OverviewTable from './OverviewTable'
import { getTableColumns } from './table-columns'

type ViewMode = 'overview' | 'detail' | 'by_customer' | 'by_payment_date'

interface ViewContentProps {
    viewMode: ViewMode
    paymentDateSubView: 'overview' | 'detail'
    filters: Record<string, any>
    apiFilters: Record<string, any>
    debouncedSearch?: string
    // Detail view props
    data: RevenueCombined[]
    isLoadingDetail: boolean
    isFetchingDetail: boolean
    params: { page?: number; page_size?: number }
    pagination?: { total: number }
    searchInputProps: Record<string, any>
    setParams: (p: Record<string, any>, replace?: boolean) => void
    setFilter: (key: any, value: any) => void
    // Export
    isExporting: boolean
    handleExport: () => void
}

/** Payment date view with overview/detail sub-toggle */
function PaymentDateView({
    paymentDateSubView,
    filters,
    apiFilters,
    debouncedSearch,
    setFilter,
    t,
}: {
    paymentDateSubView: 'overview' | 'detail'
    filters: Record<string, any>
    apiFilters: Record<string, any>
    debouncedSearch?: string
    setFilter: (key: any, value: any) => void
    t: (key: string) => string
}) {
    return (
        <div className="space-y-2">
            <div className="flex gap-1 bg-gray-100 rounded-sm p-0.5 w-fit">
                <Button
                    onClick={() => setFilter('pdv', 'overview')}
                    variant={
                        paymentDateSubView === 'overview' ? 'contained' : 'text'
                    }
                    size="small"
                    className="rounded-sm"
                >
                    <LayoutGrid className="h-3.5 w-3.5 mr-1" />
                    {t('overview.label')}
                </Button>
                <Button
                    onClick={() => setFilter('pdv', 'detail')}
                    variant={
                        paymentDateSubView === 'detail' ? 'contained' : 'text'
                    }
                    size="small"
                    className="rounded-sm"
                >
                    <List className="h-3.5 w-3.5 mr-1" />
                    {t('detail')}
                </Button>
            </div>
            {paymentDateSubView === 'overview' ? (
                <PaymentDateOverviewTable
                    filters={{
                        from_date: filters.gte
                            ? dayjs(filters.gte)
                                  .startOf('month')
                                  .format('YYYY-MM-DD')
                            : undefined,
                        to_date: filters.lte
                            ? dayjs(filters.lte)
                                  .endOf('month')
                                  .format('YYYY-MM-DD')
                            : undefined,
                        product_group_names: filters.product_group_names,
                        account_manager_codes: filters.account_manager_codes,
                        sale_manager_codes: filters.sale_manager_codes,
                        is_allocated: filters.is_allocated,
                        search: debouncedSearch || undefined,
                    }}
                />
            ) : (
                <PaymentDateTable
                    filters={{
                        ...apiFilters,
                        is_actual_revenue: filters.is_actual_revenue,
                    }}
                    search={debouncedSearch}
                />
            )}
        </div>
    )
}

/** Renders the appropriate table based on viewMode */
export function ViewContent({
    viewMode,
    paymentDateSubView,
    filters,
    apiFilters,
    debouncedSearch,
    data,
    isLoadingDetail,
    isFetchingDetail,
    params,
    pagination,
    searchInputProps,
    setParams,
    setFilter,
    isExporting,
    handleExport,
}: ViewContentProps) {
    const { t } = useTranslation('report')
    const columns = useMemo(() => getTableColumns(t), [t])

    switch (viewMode) {
        case 'by_payment_date':
            return (
                <PaymentDateView
                    paymentDateSubView={paymentDateSubView}
                    filters={filters}
                    apiFilters={apiFilters}
                    debouncedSearch={debouncedSearch}
                    setFilter={setFilter}
                    t={t}
                />
            )
        case 'by_customer':
            return (
                <CustomerGroupTable
                    filters={{
                        ...apiFilters,
                        is_actual_revenue: filters.is_actual_revenue,
                    }}
                    search={debouncedSearch}
                />
            )
        case 'overview':
            return (
                <OverviewTable
                    filters={{
                        gte: apiFilters.gte,
                        lte: apiFilters.lte,
                        product_group_names: filters.product_group_names,
                        sale_manager_codes: filters.sale_manager_codes,
                        account_manager_codes: filters.account_manager_codes,
                        is_actual_revenue: filters.is_actual_revenue,
                        search: debouncedSearch || undefined,
                    }}
                />
            )
        case 'detail':
            return (
                <DataTableCustomized<RevenueCombined>
                    columns={columns}
                    rows={data}
                    loading={isLoadingDetail}
                    isFetching={isFetchingDetail && !isLoadingDetail}
                    maxHeight="calc(100vh - 380px)"
                    pinnedHeader
                    rowKey={(row, index) =>
                        `${row.invoice_id || 'projected'}-${row.revenue_month}-${row.service_id}-${index}`
                    }
                    resizable
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_by_customer_service'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'revenue-allocations-detail',
                            actionComponents: [
                                <Button
                                    key="export"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    variant="outlined"
                                    size="small"
                                    className="rounded-sm"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    {isExporting
                                        ? t('downloading')
                                        : t('download')}
                                </Button>,
                            ],
                        },
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: (p) => setParams({ page: p }, false),
                        onPageSizeChange: (size) =>
                            setParams({ page_size: size, page: 1 }),
                        pageSizeOptions: [20, 50, 100],
                    }}
                    emptyMessage={t('no_data')}
                />
            )
    }
}
